<template>
  <section class="workspace-stack">
    <div class="workspace-grid workspace-grid--split">
      <article class="glass-surface workspace-panel">
        <div class="workspace-panel__header">
          <div>
            <span class="workspace-kicker">Etapas</span>
            <h2 class="workspace-title">Control de etapas y codigos</h2>
            <p class="workspace-copy">La etapa define el precio. Puede mostrarse en web o quedar privada, puede haber varias visibles al mismo tiempo, y cada una puede tener cero, uno o muchos codigos asociados.</p>
          </div>
          <div class="workspace-badge-row">
            <span class="workspace-badge workspace-badge--mint"><i class="fas fa-ticket-alt"></i> {{ activeStages.length }} activas</span>
            <span class="workspace-badge"><i class="fas fa-eye-slash"></i> {{ hiddenStages.length }} ocultas</span>
          </div>
        </div>

        <div class="workspace-metrics">
          <article class="workspace-metric">
            <small>Total etapas</small>
            <strong>{{ tickets.length }}</strong>
          </article>
          <article class="workspace-metric">
            <small>Codigos activos</small>
            <strong>{{ codes.length }}</strong>
          </article>
          <article class="workspace-metric">
            <small>Modo dominante</small>
            <strong>{{ modeHeadline }}</strong>
          </article>
        </div>
      </article>

      <article class="glass-surface workspace-panel">
        <div class="workspace-panel__header">
          <div>
            <span class="workspace-kicker">Editor</span>
            <h2 class="workspace-title">{{ editingTicketId ? 'Editar etapa' : 'Crear etapa' }}</h2>
          </div>
        </div>

        <form class="workspace-form workspace-form--two" @submit.prevent="submitStage">
          <label class="workspace-field">
            <span>Nombre</span>
            <input v-model="form.name" class="workspace-input" type="text" placeholder="General Early Bird" required />
          </label>

          <label class="workspace-field">
            <span>Precio</span>
            <input v-model.number="form.price" class="workspace-input" type="number" min="0" step="0.01" required />
          </label>

          <label class="workspace-field">
            <span>Cantidad inicial</span>
            <input v-model.number="form.initial_quantity" class="workspace-input" type="number" min="1" required />
          </label>

          <label class="workspace-field">
            <span>Maximo por compra</span>
            <input v-model.number="form.max_quantity" class="workspace-input" type="number" min="1" required />
          </label>

          <label class="workspace-field workspace-field--wide">
            <span>Descripcion</span>
            <textarea v-model="form.description" class="workspace-input workspace-input--textarea" rows="3" placeholder="Que incluye y para quien aplica esta etapa"></textarea>
          </label>

          <label class="workspace-field">
            <span>Tipo de ticket</span>
            <select v-model.number="form.type" class="workspace-input workspace-select">
              <option :value="1">Normal</option>
              <option :value="2">Discount</option>
              <option :value="3">Free</option>
              <option :value="4">Physical</option>
            </select>
          </label>

          <label class="workspace-field">
            <span>Fuente</span>
            <select v-model.number="form.category" class="workspace-input workspace-select">
              <option :value="1">FB / IG</option>
              <option :value="2">Tiktok</option>
              <option :value="3">Mail</option>
              <option :value="4">Ambassador</option>
              <option :value="5">Page</option>
            </select>
          </label>

          <label class="workspace-field">
            <span>Disponible desde</span>
            <input v-model="form.sales_date" class="workspace-input" type="datetime-local" />
          </label>

          <label class="workspace-field">
            <span>Disponible hasta</span>
            <input v-model="form.expiration_date" class="workspace-input" type="datetime-local" />
          </label>

          <label class="workspace-field workspace-field--wide">
            <span>Alcance de codigos</span>
            <select v-model.number="form.code_mode" class="workspace-input workspace-select">
              <option :value="0">Sin codigos</option>
              <option :value="1">Aplica a todos los codigos</option>
              <option :value="2">Aplica solo a codigos seleccionados</option>
            </select>
          </label>

          <label v-if="form.code_mode === 2" class="workspace-field workspace-field--wide">
            <span>Codigos permitidos</span>
            <select v-model="form.access_code_ids" class="workspace-input workspace-select workspace-select--multi" multiple>
              <option v-for="eventCode in activeCodes" :key="eventCode.id" :value="eventCode.id">{{ eventCode.code }} - {{ eventCode.label || 'Sin etiqueta' }}</option>
            </select>
            <small class="workspace-field__note">Un mismo codigo puede vivir en varias etapas. La visibilidad en home/web se define aparte con el checkbox.</small>
          </label>

          <div class="workspace-check-grid workspace-field--wide">
            <label class="workspace-inline-check">
              <input v-model="form.status" type="checkbox" />
              <span>Etapa activa</span>
            </label>
            <label class="workspace-inline-check">
              <input v-model="form.web_experience" type="checkbox" />
              <span>Visible en home / web publica</span>
            </label>
            <label class="workspace-inline-check">
              <input v-model="form.is_courtesy" type="checkbox" />
              <span>Cortesia</span>
            </label>
          </div>

          <div class="workspace-actions workspace-field--wide">
            <button class="workspace-button workspace-button--primary" type="submit" :disabled="saving">
              <i class="fas fa-save"></i>
              <span>{{ saving ? 'Guardando...' : editingTicketId ? 'Actualizar etapa' : 'Crear etapa' }}</span>
            </button>
            <button v-if="editingTicketId" class="workspace-button" type="button" @click="resetForm">
              <i class="fas fa-rotate-left"></i>
              <span>Cancelar edicion</span>
            </button>
          </div>
        </form>
      </article>
    </div>

    <p v-if="successMessage" class="workspace-feedback workspace-feedback--success">{{ successMessage }}</p>
    <p v-if="errorMessage" class="workspace-feedback workspace-feedback--error">{{ errorMessage }}</p>

    <article class="glass-surface workspace-panel">
      <div class="workspace-panel__header">
        <div>
          <span class="workspace-kicker">Inventario</span>
          <h2 class="workspace-title">Etapas actuales</h2>
        </div>
      </div>

      <div v-if="loading && !tickets.length" class="workspace-empty">Cargando etapas...</div>
      <div v-else-if="!tickets.length" class="workspace-empty">Todavia no hay etapas creadas. Crea la primera y define si aplica sin codigo, con todos los codigos o solo con algunos.</div>
      <div v-else class="workspace-stage-grid">
        <article v-for="ticket in tickets" :key="ticket.id" class="workspace-stage-card">
          <div class="workspace-stage-card__top">
            <div>
              <div class="workspace-badge-row workspace-badge-row--compact">
                <span class="workspace-badge">{{ codeModeLabel(ticket.code_mode) }}</span>
                <span class="workspace-badge workspace-badge--mint">{{ ticket.status ? 'Activa' : 'Oculta' }}</span>
              </div>
              <h3>{{ ticket.name }}</h3>
              <p>{{ ticket.description || 'Sin descripcion.' }}</p>
            </div>
            <div class="workspace-stage-card__price">
              <span>Precio</span>
              <strong>${{ formatMoney(ticket.price) }}</strong>
            </div>
          </div>

          <div class="workspace-stage-card__meta">
            <span><i class="fas fa-box-open"></i> Stock {{ ticket.initial_quantity }}</span>
            <span><i class="fas fa-cart-plus"></i> Max {{ ticket.max_quantity }}</span>
            <span><i class="fas fa-eye"></i> {{ stageVisibilityLabel(ticket) }}</span>
            <span><i class="fas fa-clock"></i> {{ formatDate(ticket.sales_date) }}</span>
          </div>

          <div v-if="ticket.access_codes_detail && ticket.access_codes_detail.length" class="workspace-chip-row">
            <span v-for="eventCode in ticket.access_codes_detail" :key="eventCode.id" class="workspace-chip">{{ eventCode.code }}</span>
          </div>

          <div class="workspace-code-card__actions">
            <button class="workspace-chip-button" type="button" @click="editStage(ticket)">
              <i class="fas fa-pen"></i>
              <span>Editar</span>
            </button>
            <button class="workspace-chip-button" type="button" @click="deleteStage(ticket)">
              <i class="fas fa-trash-alt"></i>
              <span>{{ ticket.status ? 'Archivar' : 'Eliminar' }}</span>
            </button>
          </div>
        </article>
      </div>
    </article>
  </section>
</template>

<script>
import { apiService } from '../../common/api.services.js'

const defaultForm = () => ({
  name: '',
  description: '',
  initial_quantity: 100,
  price: 0,
  status: true,
  web_experience: true,
  is_courtesy: false,
  type: 1,
  category: 5,
  sales_date: '',
  expiration_date: '',
  require_ig: false,
  quantity: 1,
  max_quantity: 5,
  code_mode: 0,
  access_code_ids: [],
})

export default {
  name: 'StagesView',

  props: {
    event: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      saving: false,
      tickets: [],
      codes: [],
      editingTicketId: null,
      successMessage: '',
      errorMessage: '',
      form: defaultForm(),
    }
  },

  computed: {
    activeStages() {
      return this.tickets.filter((ticket) => ticket.status)
    },

    hiddenStages() {
      return this.tickets.filter((ticket) => !ticket.status)
    },

    activeCodes() {
      return this.codes.filter((code) => code.is_active)
    },

    modeHeadline() {
      const selectedCount = this.tickets.filter((ticket) => Number(ticket.code_mode) === 2).length
      const allCount = this.tickets.filter((ticket) => Number(ticket.code_mode) === 1).length

      if (selectedCount > 0) {
        return 'Seleccionados'
      }

      if (allCount > 0) {
        return 'Todos'
      }

      return 'Publico'
    },
  },

  created() {
    this.fetchAll()
  },

  watch: {
    event: {
      deep: true,
      handler() {
        this.fetchAll()
      },
    },
  },

  methods: {
    fetchAll() {
      if (!this.event || !this.event.id) {
        return
      }

      this.loading = true
      Promise.all([
        apiService(`/api/host/events/${this.event.id}/tickets/`),
        apiService(`/api/host/events/${this.event.id}/codes/`),
      ]).then(([tickets, codes]) => {
        this.tickets = Array.isArray(tickets) ? tickets : []
        this.codes = Array.isArray(codes) ? codes : []
      }).finally(() => {
        this.loading = false
      })
    },

    resetMessages() {
      this.successMessage = ''
      this.errorMessage = ''
    },

    formatMoney(value) {
      const parsed = Number.parseFloat(value)
      return Number.isNaN(parsed) ? '0.00' : parsed.toFixed(2)
    },

    formatDate(value) {
      if (!value) {
        return 'Sin fecha'
      }

      return new Intl.DateTimeFormat('default', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
    },

    formatInputDate(value) {
      if (!value) {
        return ''
      }

      const date = new Date(value)
      const pad = (number) => `${number}`.padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
    },

    codeModeLabel(codeMode) {
      if (Number(codeMode) === 1) {
        return 'Todos los codigos'
      }
      if (Number(codeMode) === 2) {
        return 'Codigos seleccionados'
      }
      return 'Publica'
    },

    stageVisibilityLabel(ticket) {
      if (ticket.web_experience && Number(ticket.code_mode) === 0) {
        return 'Visible en home'
      }
      if (ticket.web_experience && Number(ticket.code_mode) === 1) {
        return 'Visible en home + todos los codigos'
      }
      if (ticket.web_experience && Number(ticket.code_mode) === 2) {
        return 'Visible en home + codigos elegidos'
      }
      if (Number(ticket.code_mode) === 1 || Number(ticket.code_mode) === 2) {
        return 'Privada con codigos'
      }
      return 'Oculta'
    },

    normalizedPayload() {
      const selectedCode = this.codes.find((code) => code.id === Number(this.form.access_code_ids[0]))
      return {
        ...this.form,
        code_mode: Number(this.form.code_mode),
        type: Number(this.form.type),
        category: Number(this.form.category),
        initial_quantity: Number(this.form.initial_quantity),
        price: Number(this.form.price),
        quantity: Number(this.form.quantity),
        max_quantity: Number(this.form.max_quantity),
        web_experience: Boolean(this.form.web_experience),
        require_ig: false,
        sales_date: this.form.sales_date ? `${this.form.sales_date}:00` : null,
        expiration_date: this.form.expiration_date ? `${this.form.expiration_date}:00` : null,
        access_code_ids: Number(this.form.code_mode) === 2 ? this.form.access_code_ids : [],
        code: Number(this.form.code_mode) === 2 && selectedCode ? selectedCode.code : '',
      }
    },

    submitStage() {
      this.resetMessages()
      this.saving = true

      const endpoint = this.editingTicketId
        ? `/api/host/events/tickets/${this.editingTicketId}/`
        : `/api/host/events/${this.event.id}/tickets/`

      apiService(endpoint, this.editingTicketId ? 'PATCH' : 'POST', this.normalizedPayload()).then((data) => {
        if (data && !data.err) {
          this.successMessage = this.editingTicketId ? 'Etapa actualizada.' : 'Etapa creada.'
          this.resetForm()
          this.fetchAll()
          return
        }

        this.errorMessage = (data && data.err) || 'No se pudo guardar la etapa.'
      }).finally(() => {
        this.saving = false
      })
    },

    editStage(ticket) {
      this.resetMessages()
      this.editingTicketId = ticket.id
      this.form = {
        name: ticket.name || '',
        description: ticket.description || '',
        initial_quantity: ticket.initial_quantity || 0,
        price: Number.parseFloat(ticket.price) || 0,
        status: Boolean(ticket.status),
        web_experience: Boolean(ticket.web_experience),
        is_courtesy: Boolean(ticket.is_courtesy),
        type: Number(ticket.type) || 1,
        category: Number(ticket.category) || 5,
        sales_date: this.formatInputDate(ticket.sales_date),
        expiration_date: this.formatInputDate(ticket.expiration_date),
        require_ig: Boolean(ticket.require_ig),
        quantity: Number(ticket.quantity) || 1,
        max_quantity: Number(ticket.max_quantity) || 5,
        code_mode: Number(ticket.code_mode) || 0,
        access_code_ids: Array.isArray(ticket.access_codes_detail) ? ticket.access_codes_detail.map((code) => code.id) : [],
      }
    },

    resetForm() {
      this.editingTicketId = null
      this.form = defaultForm()
    },

    deleteStage(ticket) {
      this.resetMessages()
      apiService(`/api/host/events/tickets/${ticket.id}/`, 'DELETE').then(() => {
        this.successMessage = ticket.status ? 'Etapa archivada.' : 'Etapa eliminada.'
        if (this.editingTicketId === ticket.id) {
          this.resetForm()
        }
        this.fetchAll()
      }).catch(() => {
        this.errorMessage = 'No se pudo eliminar la etapa.'
      })
    },
  },
}
</script>

<style scoped>
.workspace-stage-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
}

.workspace-stage-card {
  display: grid;
  gap: 14px;
  padding: 18px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(223, 214, 255, 0.08);
}

.workspace-stage-card__top {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.workspace-stage-card__top h3 {
  margin: 10px 0 6px;
  color: var(--text-main);
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.9rem;
}

.workspace-stage-card__top p,
.workspace-stage-card__meta {
  color: var(--text-soft);
}

.workspace-stage-card__price {
  min-width: 110px;
  text-align: right;
}

.workspace-stage-card__price span {
  display: block;
  color: var(--text-muted);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.14em;
}

.workspace-stage-card__price strong {
  color: #cbffe8;
  font-size: 1.8rem;
}

.workspace-stage-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  font-size: 0.84rem;
}

.workspace-stage-card__meta span {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

@media (max-width: 720px) {
  .workspace-stage-card__top {
    display: grid;
  }

  .workspace-stage-card__price {
    text-align: left;
  }
}
</style>
